import { createDebug } from '@/utils/debug'
import { isPptOnlineAddin, documentScopedKey } from '@/services/office/ppt-online-binding'

// AHA-43111: cross-frame handover of "which slide is on screen" in the PowerPoint Online
// Slide Show.
//
// Split out of ppt-online-binding because the two solve different problems on the same
// transport. That module is a STORE: durable per-slide bindings written in the edit session and
// read back in the show. This one is a HANDOVER: a single volatile value, meaningful only for
// the length of one Slide Show, that exists so a frame the host refuses to answer can still
// resolve itself at cold load.
//
// Why a handover is needed at all. In the Slide Show session getSelectedDataAsync answers
// exactly ONE frame per document and leaves every other caller outstanding with no callback,
// ever. Measured, not assumed: five fresh calls at 8s intervals from a starved frame all hung
// exactly like the first. Starvation is permanent for a frame within a session, so retrying
// cannot rescue one — which is why Mentimeter's retry layer is deliberately not reproduced here.
// The losing frames cannot ask, so the answer has to be pushed to them. Every add-in frame is
// same-origin (one add-in, one manifest), and the `storage` event fires in every same-origin
// context EXCEPT the writer — so the served frame publishes and every starved frame is notified
// without touching Office at all.
//
// SCOPE: cold load only. This deliberately does NOT follow the presenter advancing slides. An
// earlier revision republished on a 1s timer so starved frames could keep up with slide changes;
// it was removed because nothing consumed the result — the store field the routing writes
// (powerPointSlideId) is only read when the presentation is FETCHED, so a frame already loaded
// on its presentation ignored every later update. The timer cost one Office call per second and
// woke every other frame into a redundant remap API call on each tick, all of it invisible. If
// follow-along is wanted later it needs the observer first, then the timer — in that order.
//
// Pushing a document-global value to every frame is not a compromise here, it is the correct
// answer for the cold load: the show displays one slide at a time, so the frame whose slide is
// current is the only one a viewer can see.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:load-route' (or 'aha:ppt:*'). Shares the
// cold-load routing namespace on purpose — publishing and routing are one trace to read.
const log = createDebug('ppt:load-route')

const CURRENT_SLIDE_KEY_PREFIX = 'aha:ppt:show-current-slide:'

const getCurrentSlideKey = () => documentScopedKey(CURRENT_SLIDE_KEY_PREFIX)

// An entry outlives the Slide Show that wrote it, so a stale one from a PREVIOUS run would resolve
// this run's frames to whatever slide that run ended on. Only the direct read is age-checked;
// values arriving via the `storage` event are fresh by construction.
const CURRENT_SLIDE_MAX_AGE_MS = 60000

export const publishShowCurrentSlideId = (commonSlideId) => {
  if (!isPptOnlineAddin()) return
  const key = getCurrentSlideKey()
  if (!key || commonSlideId === null || commonSlideId === undefined || commonSlideId === '') return
  try {
    localStorage.setItem(key, JSON.stringify({ commonSlideId, at: Date.now() }))
    log('current show slide published |', commonSlideId)
  } catch (error) {
    log('publish current show slide failed', error)
  }
}

// The winner may have published before this frame armed its listener, so the listener alone would
// miss it. Returns the Common-API slide id, or null when absent or too old to trust.
export const readShowCurrentSlideId = () => {
  if (!isPptOnlineAddin()) return null
  const key = getCurrentSlideKey()
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.commonSlideId) return null
    const age = Date.now() - (parsed.at || 0)
    if (age > CURRENT_SLIDE_MAX_AGE_MS) {
      log('current show slide ignored | stale by', age, 'ms |', parsed.commonSlideId)
      return null
    }
    log('current show slide read |', parsed.commonSlideId, '| age', age, 'ms')
    return parsed.commonSlideId
  } catch (error) {
    log('read current show slide failed', error)
    return null
  }
}

// Calls handler(commonSlideId) whenever another frame publishes. Returns an unsubscribe function.
const subscribeShowCurrentSlideId = (handler) => {
  const key = getCurrentSlideKey()
  if (!key) return () => {}
  const onStorage = (event) => {
    if (event.key !== key || !event.newValue) return
    try {
      const parsed = JSON.parse(event.newValue)
      if (!parsed || !parsed.commonSlideId) return
      log('current show slide received from another frame |', parsed.commonSlideId)
      handler(parsed.commonSlideId)
    } catch (error) {
      log('current show slide event unparsable', error)
    }
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}

// Arms the cross-frame handover for a frame that could not get its own answer, and disarms it once
// the frame is resolved.
//
// `handler` reports whether the delivery actually resolved this frame (truthy, or a promise of it).
// Unsubscribing on SUCCESS rather than on first delivery is the point: a broadcast id with no
// stored binding is a miss, and burning the subscription on it would strand a frame that the very
// next publish could have resolved. Latched, because the caller can reach this from more than one
// place on the cold-load path, and because a resolved frame must never re-arm.
let showCurrentSlideFollowStarted = false
export const followBroadcastCurrentSlide = (handler) => {
  if (showCurrentSlideFollowStarted) return
  showCurrentSlideFollowStarted = true
  const unsubscribe = subscribeShowCurrentSlideId((commonSlideId) => {
    Promise.resolve(handler(commonSlideId)).then((resolved) => {
      if (!resolved) return null
      unsubscribe()
      log('cold load resolved from a broadcast -> unsubscribed |', commonSlideId)
      return null
    }).catch((error) => log('broadcast handler failed', error))
  })
}
